
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Vitoria {
  id: string;
  nome: string;
  conquista: string;
  como_conquistou: string;
  tecnica_usada: string;
  curtidas: number;
  created_at: string;
  foto_url?: string;
  video_url?: string;
}

export default function VitoriasSection() {
  const [vitorias, setVitorias] = useState<Vitoria[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [curtindo, setCurtindo] = useState<string | null>(null);
  const [vitoriasLikes, setVitoriasLikes] = useState<Record<string, boolean>>({});

  useEffect(() => {
    carregarVitorias();
    carregarCurtidasLocais();
  }, []);

  const carregarVitorias = async () => {
    try {
      const { data, error } = await supabase
        .from('vitorias')
        .select('*')
        .eq('aprovado', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVitorias(data || []);
    } catch (error) {
      console.error('Erro ao carregar vitórias:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarCurtidasLocais = () => {
    try {
      const likes = localStorage.getItem('vitorias_curtidas');
      if (likes) {
        setVitoriasLikes(JSON.parse(likes));
      }
    } catch (e) {
      console.warn('Falha ao ler curtidas do localStorage:', e);
    }
  };

  const handleCurtir = async (id: string) => {
    if (vitoriasLikes[id]) {
      return;
    }

    setCurtindo(id);

    try {
      const { error } = await supabase.rpc('incrementar_curtidas_vitoria', {
        vitoria_id: id,
      });

      if (error) throw error;

      setVitorias(prev =>
        prev.map(v => (v.id === id ? { ...v, curtidas: v.curtidas + 1 } : v))
      );

      const novasLikes = { ...vitoriasLikes, [id]: true };
      setVitoriasLikes(novasLikes);
      localStorage.setItem('vitorias_curtidas', JSON.stringify(novasLikes));
    } catch (error) {
      console.error('Erro ao curtir:', error);
    } finally {
      setCurtindo(null);
    }
  };

  const vitoriasExibidas = showAll ? vitorias : vitorias.slice(0, 3);

  if (loading) {
    return (
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center text-amber-400">
            <i className="ri-loader-4-line text-4xl animate-spin"></i>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 rounded-full mb-6">
            <i className="ri-trophy-line text-amber-400 text-xl"></i>
            <span className="text-amber-400 font-['Montserrat'] text-sm font-semibold tracking-wider uppercase">
              Histórias de Transformação
            </span>
          </div>
          <h2 className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold text-white mb-6">
            Mural de{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              Vitórias
            </span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto font-['Montserrat']">
            Conheça as histórias reais de pessoas que transformaram suas vidas usando as
            técnicas do método
          </p>
        </div>

        {/* Grid de Vitórias */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {vitoriasExibidas.map(vitoria => (
            <div
              key={vitoria.id}
              className="group bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-8 hover:border-amber-500/40 transition-all duration-500 hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]"
            >
              {/* Mídia */}
              {vitoria.foto_url && (
                <div className="mb-6 rounded-xl overflow-hidden">
                  <img
                    src={vitoria.foto_url}
                    alt={vitoria.conquista}
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}
              {vitoria.video_url && (
                <div className="mb-6 rounded-xl overflow-hidden">
                  <video src={vitoria.video_url} controls className="w-full h-48 object-cover" />
                </div>
              )}

              {/* Técnica usada */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4">
                <i className="ri-lightbulb-line text-amber-400 text-sm"></i>
                <span className="text-amber-400 text-xs font-['Montserrat'] font-semibold uppercase tracking-wider">
                  {vitoria.tecnica_usada}
                </span>
              </div>

              {/* Nome */}
              <h3 className="font-['Montserrat'] text-xl font-bold text-white mb-3">
                {vitoria.nome}
              </h3>

              {/* Conquista */}
              <p className="text-white/80 font-['Montserrat'] text-base leading-relaxed mb-4">
                <span className="text-amber-400 font-semibold">Conquistou:</span>{' '}
                {vitoria.conquista}
              </p>

              {/* Como conquistou (expansível) */}
              {expandedId === vitoria.id && (
                <div className="mb-4 p-4 bg-black/30 border border-amber-500/20 rounded-lg">
                  <p className="text-white/70 font-['Montserrat'] text-sm leading-relaxed">
                    <span className="text-amber-400 font-semibold block mb-2">
                      Como conquistou:
                    </span>
                    {vitoria.como_conquistou}
                  </p>
                </div>
              )}

              {/* Botão expandir */}
              <button
                onClick={() =>
                  setExpandedId(expandedId === vitoria.id ? null : vitoria.id)
                }
                className="text-amber-400 hover:text-amber-300 font-['Montserrat'] text-sm font-semibold mb-4 transition-colors cursor-pointer flex items-center gap-2"
              >
                <i
                  className={`ri-arrow-${expandedId === vitoria.id ? 'up' : 'down'}-s-line`}
                ></i>
                {expandedId === vitoria.id ? 'Ocultar detalhes' : 'Ver como conquistou'}
              </button>

              {/* Footer com curtidas e data */}
              <div className="flex items-center justify-between pt-4 border-t border-amber-500/20">
                <button
                  onClick={() => handleCurtir(vitoria.id)}
                  disabled={vitoriasLikes[vitoria.id] || curtindo === vitoria.id}
                  className={`flex items-center gap-2 transition-all duration-300 cursor-pointer ${
                    vitoriasLikes[vitoria.id]
                      ? 'text-amber-400'
                      : 'text-white/40 hover:text-amber-400'
                  } ${curtindo === vitoria.id ? 'animate-pulse' : ''}`}
                >
                  <i
                    className={`${
                      vitoriasLikes[vitoria.id] ? 'ri-heart-fill' : 'ri-heart-line'
                    } text-xl ${curtindo === vitoria.id ? 'animate-bounce' : ''}`}
                  ></i>
                  <span className="font-['Montserrat'] text-sm font-semibold">
                    {vitoria.curtidas}
                  </span>
                </button>
                <span className="text-white/40 text-xs font-['Montserrat']">
                  {new Date(vitoria.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Botões de ação */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {vitorias.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-8 py-4 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border-2 border-amber-500/50 text-amber-400 font-['Montserrat'] text-sm font-semibold tracking-wider uppercase rounded-lg hover:border-amber-400 hover:shadow-[0_0_20px_rgba(245,158,11,0.2)] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {showAll ? 'Ver Menos' : `Ver Todas as ${vitorias.length} Vitórias`}
            </button>
          )}
          <a
            href="/vitorias"
            className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-gray-900 font-['Montserrat'] text-sm font-semibold tracking-wider uppercase rounded-lg hover:shadow-[0_0_30px_rgba(245,158,11,0.3)] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-trophy-line mr-2"></i>
            Compartilhe Sua Vitória
          </a>
        </div>
      </div>
    </section>
  );
}
